import type { FiltrosMontagem, PessoaMontagem } from './types';

export const FILTROS_INICIAIS: FiltrosMontagem = {
  busca: '',
  talentos: '',
  soSemEquipe: false,
  primeiroServir: false,
  jaServiu: false,
  fica5Dias: false,
  vaiPastoreio: false,
  sexo: null,
  cidade: '',
  paroquia: '',
  temFilhos: false,
  temRestricao: false,
};

// Converte uma linha de vw_montagem / vw_sem_equipe (sem nota/por_que).
export function deMontagem(row: Record<string, unknown>): PessoaMontagem {
  return {
    inscricao_id: Number(row.inscricao_id),
    nome: (row.nome as string) ?? '',
    telefone: (row.telefone as string) ?? null,
    email: (row.email as string) ?? null,
    foto_url: (row.foto_url as string) ?? null,
    sexo: (row.sexo as string) ?? null,
    idade: (row.idade as number) ?? null,
    cidade: (row.cidade as string) ?? null,
    paroquia: (row.paroquia as string) ?? null,
    talentos: (row.talentos as string) ?? null,
    profissao: (row.profissao as string) ?? null,
    trabalho: (row.trabalho as string) ?? null,
    escolaridade: (row.escolaridade as string) ?? null,
    equipe_id: (row.equipe_id as number) ?? null,
    equipe_atual: (row.equipe as string) ?? null,
    status_alocacao: (row.status_alocacao as string) ?? null,
    primeiro_servir: !!row.primeiro_servir,
    todos_os_dias: !!row.todos_os_dias,
    vai_ao_pastoreio: !!row.vai_ao_pastoreio,
    tem_filhos: !!row.tem_filhos,
    tem_restricao: !!row.tem_restricao,
    filhos: (row.filhos as string) ?? null,
    restricao_alimentar: (row.restricao_alimentar as string) ?? null,
    disponibilidade: (row.disponibilidade as string[]) ?? null,
    ja_serviu_equipes: (row.ja_serviu_equipes as string[]) ?? null,
    nao_quer_equipes: (row.nao_quer_equipes as string[]) ?? null,
    topa_qualquer_equipe: !!row.topa_qualquer_equipe,
    quantas_equipes_ja_serviu: (row.quantas_equipes_ja_serviu as number) ?? null,
    camiseta_tamanho: (row.camiseta_tamanho as string) ?? null,
    nota: null,
    por_que: null,
    recusou: false,
    ja_serviu_aqui: false,
  };
}

// Converte uma linha de candidatos_para_equipe (com nota/por_que).
export function deCandidato(row: Record<string, unknown>): PessoaMontagem {
  return {
    inscricao_id: Number(row.inscricao_id),
    nome: (row.nome as string) ?? '',
    telefone: (row.telefone as string) ?? null,
    email: (row.email as string) ?? null,
    foto_url: (row.foto_url as string) ?? null,
    sexo: (row.sexo as string) ?? null,
    idade: (row.idade as number) ?? null,
    cidade: (row.cidade as string) ?? null,
    paroquia: (row.paroquia as string) ?? null,
    talentos: (row.talentos as string) ?? null,
    profissao: (row.profissao as string) ?? null,
    trabalho: (row.trabalho as string) ?? null,
    escolaridade: (row.escolaridade as string) ?? null,
    equipe_id: (row.equipe_id as number) ?? null,
    equipe_atual: (row.equipe_atual as string) ?? null,
    status_alocacao: (row.status_alocacao as string) ?? null,
    primeiro_servir: !!row.primeiro_servir,
    todos_os_dias: !!row.todos_os_dias,
    vai_ao_pastoreio: !!row.vai_ao_pastoreio,
    tem_filhos: !!row.tem_filhos,
    tem_restricao: !!row.tem_restricao,
    filhos: (row.filhos as string) ?? null,
    restricao_alimentar: (row.restricao_alimentar as string) ?? null,
    disponibilidade: (row.disponibilidade as string[]) ?? null,
    ja_serviu_equipes: (row.ja_serviu_equipes as string[]) ?? null,
    nao_quer_equipes: (row.nao_quer_equipes as string[]) ?? null,
    topa_qualquer_equipe: !!row.topa_qualquer_equipe,
    quantas_equipes_ja_serviu: (row.quantas_equipes_ja_serviu as number) ?? null,
    camiseta_tamanho: (row.camiseta_tamanho as string) ?? null,
    nota: (row.nota as number) ?? null,
    por_que: (row.por_que as string) ?? null,
    recusou: !!row.recusou,
    ja_serviu_aqui: !!row.ja_serviu_aqui,
  };
}

// Mantém a ordem de "nota" vinda do banco, apenas agrupando: quem já está
// nesta equipe no topo e quem recusou no fim. Sort estável preserva a ordem.
export function ordenarEquipe(todos: PessoaMontagem[], equipeId: number): PessoaMontagem[] {
  return [...todos].sort((a, b) => {
    const rank = (p: PessoaMontagem) => {
      if (p.equipe_id === equipeId) return 0;
      if (p.recusou) return 2;
      return 1;
    };
    return rank(a) - rank(b);
  });
}

function inclui(valor: string | null | undefined, texto: string): boolean {
  return (valor || '').toLowerCase().includes(texto.trim().toLowerCase());
}

// Filtro idêntico nos dois modos (client-side no modo equipe).
export function filtra(p: PessoaMontagem, f: FiltrosMontagem): boolean {
  if (f.busca && !inclui(p.nome, f.busca)) return false;
  if (f.talentos && !inclui(p.talentos, f.talentos)) return false;
  if (f.soSemEquipe && p.equipe_id !== null) return false;
  if (f.primeiroServir && !p.primeiro_servir) return false;
  if (f.jaServiu && !((p.quantas_equipes_ja_serviu ?? 0) > 0)) return false;
  if (f.fica5Dias && !p.todos_os_dias) return false;
  if (f.vaiPastoreio && !p.vai_ao_pastoreio) return false;
  if (f.sexo && p.sexo !== f.sexo) return false;
  if (f.cidade && !inclui(p.cidade, f.cidade)) return false;
  if (f.paroquia && !inclui(p.paroquia, f.paroquia)) return false;
  if (f.temFilhos && !p.tem_filhos) return false;
  if (f.temRestricao && !p.tem_restricao) return false;
  return true;
}

export type CorEtiqueta = 'verde' | 'ambar' | 'vermelha' | 'cinza';

export function quebrarPorQue(porQue: string | null): string[] {
  if (!porQue) return [];
  return porQue
    .split('·')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function corEtiqueta(parte: string): CorEtiqueta {
  const up = parte.toUpperCase();
  if (up.startsWith('PEDIU PARA NÃO SERVIR')) return 'vermelha';
  if (up.startsWith('NÃO')) return 'ambar';
  if (parte.toLowerCase().startsWith('já está em')) return 'cinza';
  return 'verde';
}

// Iniciais para o círculo quando não há foto.
export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

// Placar "Cozinha (12/25)" ou "Cozinha (12)" quando não há vagas definidas.
export function placarEquipe(equipe: { equipe: string; alocados: number; vagas_alvo: number | null }): string {
  const alocados = Number(equipe.alocados) || 0;
  if (equipe.vagas_alvo === null || equipe.vagas_alvo === undefined) {
    return `${equipe.equipe} (${alocados})`;
  }
  return `${equipe.equipe} (${alocados}/${equipe.vagas_alvo})`;
}